import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNativePreviewUrl,
  getYouTubeVideoId,
  inferVideoSource,
  type VideoSource,
  type VideoStreams,
} from '../../lib/services/videoService';

export interface VideoCardProps {
  id: string;
  title: string;
  thumbnail: string;
  duration?: number;
  /** 'video' = 16:9, 'short' = 9:16 */
  aspectRatio?: 'video' | 'short';
  /** Show progress bar (for continue watching) */
  showProgress?: boolean;
  /** Progress percentage 0-100 */
  progress?: number;
  /** Show duration badge */
  showDuration?: boolean;
  /** Custom badge text (top-left) */
  badge?: string;
  /** Custom path to navigate to */
  href?: string;
  /** Additional className */
  className?: string;
  /** Enable hover preview (default: true for videos) */
  enablePreview?: boolean;
  /** Delay before preview starts in ms (default: 500) */
  previewDelay?: number;
  /** Video processing status — shows badge for non-ready videos */
  status?: 'uploading' | 'processing' | 'ready' | 'failed';
  /** Optional source metadata for correct preview path selection */
  source?: VideoSource;
  /** Optional YouTube ID override */
  youtubeId?: string;
  /** Optional stream metadata for native preview URL detection */
  streams?: VideoStreams;
  /** Optional direct MP4 URL for native preview */
  downloadUrl?: string;
  /** Optional legacy direct MP4 URL for native preview */
  rawUrl?: string;
  /** Channel/creator display name */
  channelName?: string;
/** Creator avatar URL */
  creatorAvatar?: string;
  /** Time ago label (e.g. "3d", "1h") */
  timeAgo?: string;
  /** Real YouTube view count (YouTube videos only) */
  viewCount?: number;
}

/** Format view count YouTube-style: 1.2K, 259K, 11M */
function formatViewCount(count: number): string {
  if (count >= 1_000_000) {
    const val = count / 1_000_000;
    return `${val >= 10 ? Math.round(val) : val.toFixed(1)}M views`;
  }
  if (count >= 1_000) {
    const val = count / 1_000;
    return `${val >= 10 ? Math.round(val) : val.toFixed(1)}K views`;
  }
  return `${count} views`;
}

// Track logged failures to avoid console spam
const loggedThumbnailFailures = new Set<string>();
let previewPreconnectAdded = false;

const ensurePreviewPreconnects = () => {
  if (previewPreconnectAdded || typeof document === 'undefined') return;
  previewPreconnectAdded = true;

  const head = document.head || document.getElementsByTagName('head')[0];
  const preconnects = [
    'https://www.youtube.com',
    'https://i.ytimg.com',
  ];

  preconnects.forEach((href) => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    link.crossOrigin = '';
    head.appendChild(link);
  });
};

function VideoCard({
  id,
  title,
  thumbnail,
  duration = 0,
  aspectRatio = 'video',
  showProgress = false,
  progress = 0,
  showDuration = true,
  badge,
  href,
  className = '',
  enablePreview = true,
  previewDelay = 0,
  status,
  source,
  youtubeId,
  streams,
  downloadUrl,
  rawUrl,
  channelName,
  creatorAvatar,
  timeAgo,
  viewCount,
}: VideoCardProps) {
  const isProcessing = status === 'processing' || status === 'uploading';
  const isFailed = status === 'failed';
  const resolvedSource = inferVideoSource({ id, source, streams, downloadUrl, rawUrl, youtubeId });
  const resolvedYouTubeId = getYouTubeVideoId({ id, source, streams, downloadUrl, rawUrl, youtubeId });
  const nativePreviewUrl = getNativePreviewUrl({ id, source, streams, downloadUrl, rawUrl, youtubeId });
  const canShowYoutubePreview = resolvedSource === 'youtube' && !!resolvedYouTubeId;
  const canShowNativePreview = resolvedSource === 'native' && !!nativePreviewUrl;
  const hasPreviewSource = canShowYoutubePreview || canShowNativePreview;
  const navigate = useNavigate();
  const [currentThumbnail, setCurrentThumbnail] = useState(thumbnail);
  const [thumbnailHidden, setThumbnailHidden] = useState(false);
  const [previewRequested, setPreviewRequested] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [canPreview, setCanPreview] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prefetchLinkRef = useRef<HTMLLinkElement | null>(null);
  const showPreview = previewRequested && previewReady;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const supportsHover = window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const connection = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;
    const saveData = connection?.saveData ?? false;
    const slowConnection =
      connection?.effectiveType === '2g' || connection?.effectiveType === 'slow-2g';
    setCanPreview(!reduceMotion && supportsHover && !saveData && !slowConnection);
  }, []);

  // Handle hover start - start timer for preview
  const handleMouseEnter = useCallback(() => {
    if (enablePreview && canPreview && hasPreviewSource) {
      if (canShowYoutubePreview) {
        ensurePreviewPreconnects();
      }
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setPreviewRequested(true);
      }, previewDelay);
    }
    // Prefetch native MP4 on hover so it's partially cached when user clicks through
    if (resolvedSource === 'native' && nativePreviewUrl && !prefetchLinkRef.current) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'video';
      link.href = nativePreviewUrl;
      document.head.appendChild(link);
      prefetchLinkRef.current = link;
    }
  }, [enablePreview, previewDelay, canPreview, hasPreviewSource, canShowYoutubePreview, resolvedSource, nativePreviewUrl]);

  // Handle hover end - clear timer and hide preview
  const handleMouseLeave = useCallback(() => {
    setPreviewRequested(false);
    setPreviewReady(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (prefetchLinkRef.current) {
      prefetchLinkRef.current.remove();
      prefetchLinkRef.current = null;
    }
  }, []);

  // Cleanup timeout and prefetch on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (prefetchLinkRef.current) {
        prefetchLinkRef.current.remove();
        prefetchLinkRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (canPreview) return;
    setPreviewRequested(false);
    setPreviewReady(false);
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, [canPreview]);

  useEffect(() => {
    setCurrentThumbnail(thumbnail);
    setThumbnailHidden(false);
  }, [thumbnail]);

  // Handle thumbnail load error with YouTube fallback
  const handleThumbnailError = useCallback(() => {
    // Only attempt YouTube fallback cascade for YouTube-sourced videos
    if (resolvedSource === 'youtube') {
      // Check if it's a YouTube maxresdefault URL
      const maxresMatch = currentThumbnail.match(/img\.youtube\.com\/vi\/([^/]+)\/maxresdefault\.jpg/);
      if (maxresMatch) {
        setCurrentThumbnail(`https://img.youtube.com/vi/${maxresMatch[1]}/hqdefault.jpg`);
        return;
      }

      // Skip mqdefault — hqdefault is the last useful fallback.
      // mqdefault always exists on YouTube but is tiny/blurry, so failing hqdefault
      // means the video is likely deleted/private. Hide immediately.
    }

    // All fallbacks exhausted or non-YouTube URL - hide thumbnail
    setThumbnailHidden(true);
    if (!loggedThumbnailFailures.has(id)) {
      loggedThumbnailFailures.add(id);
    }
  }, [currentThumbnail, id, resolvedSource]);

  // Nuke unavailable YouTube videos — no thumbnail = deleted/private
  if (thumbnailHidden && resolvedSource === 'youtube') return null;
  // Also nuke if thumbnail was never provided for a YouTube video
  if (!thumbnail && resolvedSource === 'youtube') return null;

  const formatDuration = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClick = () => {
    // Don't navigate to non-ready videos
    if (isProcessing || isFailed) return;
    if (href) {
      navigate(href);
    } else if (aspectRatio === 'short') {
      navigate(`/shorts?v=${id}`);
    } else {
      navigate(`/video/${id}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Always show metadata row — title is displayed here (not on the card)
  const hasMetadata = true;

  return (
    <div className={className}>
      {/* Thumbnail area */}
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={`Watch ${title}${duration ? `, ${formatDuration(duration)}` : ''}`}
        className={`
          relative overflow-hidden rounded-xl shadow-lg
          transition-shadow duration-200 hover:shadow-xl
          focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-transparent
          ${isProcessing || isFailed ? 'cursor-default opacity-80' : 'cursor-pointer'}
          ${aspectRatio === 'short' ? 'aspect-[9/16]' : 'aspect-video'}
        `}
      >
        {/* Thumbnail - fills entire card */}
        {!thumbnailHidden && (
          <img
            src={currentThumbnail}
            alt=""
            aria-hidden="true"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${showPreview ? 'opacity-0' : 'opacity-100'}`}
            loading="lazy"
            onError={handleThumbnailError}
          />
        )}
        {thumbnailHidden && !showPreview && (
          <div className="absolute inset-0 w-full h-full bg-gray-800 flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}

        {/* Video preview on hover */}
        {previewRequested && (
          canShowYoutubePreview && resolvedYouTubeId ? (
            <iframe
              src={`https://www.youtube.com/embed/${resolvedYouTubeId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&showinfo=0&loop=1&playlist=${resolvedYouTubeId}`}
              className="absolute inset-0 w-full h-full pointer-events-none"
              allow="autoplay; encrypted-media"
              allowFullScreen
              loading="lazy"
              onLoad={() => setPreviewReady(true)}
              title={`Preview: ${title}`}
            />
          ) : canShowNativePreview && nativePreviewUrl ? (
            <video
              src={nativePreviewUrl}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              muted
              playsInline
              autoPlay
              loop
              preload="metadata"
              onLoadedData={() => setPreviewReady(true)}
              onCanPlay={() => setPreviewReady(true)}
              onError={() => {
                setPreviewReady(false);
                setPreviewRequested(false);
              }}
              aria-hidden="true"
            />
          ) : null
        )}

        {/* Gradient overlay for text readability - hidden during preview */}
        <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none transition-opacity duration-300 ${showPreview ? 'opacity-0' : 'opacity-100'}`} />

        {/* Progress bar (optional) - hidden during preview */}
        {showProgress && !showPreview && (
          <div className="absolute bottom-0 left-0 w-full h-1 bg-white/30">
            <div
              className="h-1 bg-yellow-400 transition-all"
              style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
            />
          </div>
        )}

        {/* Duration text at bottom (no title on card) - hidden during preview */}
        {showDuration && duration > 0 && (
          <div className={`absolute bottom-2 left-3 right-3 pointer-events-none transition-opacity duration-300 ${showPreview ? 'opacity-0' : 'opacity-100'}`}>
            <p className="text-gray-300 text-xs drop-shadow-lg">
              {formatDuration(duration)}
            </p>
          </div>
        )}

        {/* Duration badge (top-right) - hidden during preview */}
        {showDuration && duration > 0 && aspectRatio === 'video' && !showPreview && (
          <div className="absolute top-2 right-2 bg-black/75 px-2 py-0.5 rounded text-xs text-white pointer-events-none">
            {formatDuration(duration)}
          </div>
        )}

        {/* Custom badge (top-left) - hidden during preview */}
        {badge && !showPreview && (
          <div className="absolute top-2 left-2 bg-black/75 px-2 py-0.5 rounded text-xs text-white pointer-events-none">
            {badge}
          </div>
        )}

        {/* Processing overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 bg-black/70 px-3 py-1.5 rounded-full">
              <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              <span className="text-xs font-medium text-white">Processing</span>
            </div>
          </div>
        )}

        {/* Failed overlay */}
        {isFailed && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 bg-red-900/80 px-3 py-1.5 rounded-full">
              <span className="text-xs font-medium text-red-200">Failed</span>
            </div>
          </div>
        )}
      </div>

      {/* Metadata row below thumbnail — unified YouTube-style for both native and YouTube */}
      {hasMetadata && (
        <div className="flex items-start gap-2.5 mt-2.5 px-1">
          {creatorAvatar && (
            <img
              src={creatorAvatar}
              alt={channelName || 'Creator'}
              className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5"
            />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium line-clamp-2 leading-snug" style={{ color: 'var(--vb-text-primary)' }}>
              {title}
            </p>
            {channelName && (
              <p className="text-xs mt-1 truncate" style={{ color: 'var(--vb-text-secondary)' }}>{channelName}</p>
            )}
            <p className="text-xs mt-0.5" style={{ color: 'var(--vb-text-secondary)' }}>
              {viewCount ? formatViewCount(viewCount) : null}
              {viewCount && timeAgo ? ' \u00B7 ' : null}
              {timeAgo || null}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default React.memo(VideoCard);
